import { BubbleDataPoint, Chart, ChartTypeRegistry, Point, TooltipModel } from 'chart.js';

import { buildTooltipHtml, mapTooltipModelDataPoints } from './buildTooltipHtml';
import type { ChartTypes } from './Legends';
import { hideChartTooltip, tooltipElementId } from './tooltipDom';

const getOrCreateTooltip = (chartId: string) => {
  const id = tooltipElementId(chartId);
  let tooltipEl = document.getElementById(id);

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.style.display = 'flex';
    tooltipEl.style.flexDirection = 'column';
    tooltipEl.style.alignItems = 'center';
    tooltipEl.style.gap = '6px';
    tooltipEl.style.width = 'fit-content';
    tooltipEl.style.minWidth = '130px';
    tooltipEl.style.height = 'fit-content';
    tooltipEl.style.backgroundColor = 'white';
    tooltipEl.style.boxShadow = '6px 4px 20px 0px rgba(0, 12, 54, 0.15)';
    tooltipEl.style.borderRadius = '4px';
    tooltipEl.style.padding = '6px 8px 6px 8px';
    tooltipEl.style.zIndex = '9999';
    tooltipEl.id = id;
    tooltipEl.setAttribute('role', 'tooltip');
    tooltipEl.setAttribute('aria-hidden', 'true');

    document.body.appendChild(tooltipEl);
  }

  return tooltipEl;
};

type ExternalTooltipContext = {
  chart: Chart<
    keyof ChartTypeRegistry,
    (number | Point | [number, number] | BubbleDataPoint | null)[],
    unknown
  >;
  tooltip: TooltipModel<'line'>;
};

export function createExternalTooltipHandler(chartType: ChartTypes, chartId: string) {
  return (context: ExternalTooltipContext) => {
    const { tooltip } = context;
    const tooltipEl = getOrCreateTooltip(chartId);
    const tooltipModel = tooltip;

    if (tooltipModel.opacity === 0 || tooltipModel.dataPoints.length === 0) {
      hideChartTooltip(chartId);
      return;
    }

    tooltipEl.classList.remove('above', 'below', 'no-transform');
    if (tooltipModel.yAlign) {
      tooltipEl.classList.add(tooltipModel.yAlign);
    } else {
      tooltipEl.classList.add('no-transform');
    }

    if (tooltipModel.dataPoints.length > 0) {
      const innerHtml = buildTooltipHtml({
        dataPoints: mapTooltipModelDataPoints(tooltipModel.dataPoints),
        chartType,
      });
      tooltipEl.innerHTML = innerHtml;
    }

    const position = context.chart.canvas.getBoundingClientRect();
    tooltipEl.style.opacity = '1';
    tooltipEl.style.position = 'absolute';
    tooltipEl.setAttribute('aria-hidden', 'false');

    const tooltipWidth = tooltipEl.offsetWidth;
    const chartWidth = position.width;
    const caretX = tooltipModel.caretX;

    let left = position.left + window.scrollX + caretX;
    if (left + tooltipWidth > position.left + window.scrollX + chartWidth) {
      left -= tooltipWidth;
    }

    tooltipEl.style.left = `${left}px`;
    tooltipEl.style.top = `${position.top + window.scrollY + tooltipModel.caretY}px`;
    tooltipEl.style.pointerEvents = 'none';
  };
}
